/**
 * Global keyboard shortcuts system for Magic MD.
 * Provides a registry of commands and a React hook to bind them.
 */

import { useEffect } from "react";

export interface Command {
    id: string;
    label: string;
    /** Keyboard shortcut display string, e.g. "⌘S" */
    shortcut?: string;
    /** Category for command palette grouping */
    category?: string;
    /** The handler to invoke */
    execute: () => void | Promise<void>;
}

type Modifier = "meta" | "ctrl" | "alt" | "shift";

interface ShortcutBinding {
    key: string;
    modifiers: Modifier[];
    commandId: string;
}

/** Global command registry */
const commands = new Map<string, Command>();
const bindings: ShortcutBinding[] = [];

/** Whether we're on macOS (⌘ = meta) vs other platforms (Ctrl) */
const isMac =
    typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

/**
 * Register a command with an optional keyboard shortcut.
 */
export function registerCommand(command: Command): void {
    // If command already exists, unregister it first to clean up old bindings
    if (commands.has(command.id)) {
        unregisterCommand(command.id);
    }
    commands.set(command.id, command);
}

/**
 * Remove a command from the registry.
 */
export function unregisterCommand(id: string): void {
    commands.delete(id);
    // Remove all bindings for this command
    for (let i = bindings.length - 1; i >= 0; i--) {
        if (bindings[i].commandId === id) {
            bindings.splice(i, 1);
        }
    }
}

/**
 * Parse a shortcut string like "Cmd+S" or "Ctrl+Shift+P" into a binding.
 */
function parseShortcut(shortcut: string, commandId: string): ShortcutBinding {
    const parts = shortcut.split("+").map((s) => s.trim().toLowerCase());
    const modifiers: Modifier[] = [];
    let key = "";

    for (const part of parts) {
        switch (part) {
            case "cmd":
            case "meta":
            case "⌘":
                modifiers.push("meta");
                break;
            case "ctrl":
            case "control":
                modifiers.push("ctrl");
                break;
            case "alt":
            case "option":
            case "⌥":
                modifiers.push("alt");
                break;
            case "shift":
            case "⇧":
                modifiers.push("shift");
                break;
            default:
                key = part;
        }
    }

    return { key, modifiers, commandId };
}

/**
 * Bind a keyboard shortcut to a command.
 */
export function bindShortcut(shortcut: string, commandId: string): void {
    // Remove existing binding for this command if it exists to prevent duplicates
    const existingIdx = bindings.findIndex((b) => b.commandId === commandId);
    if (existingIdx >= 0) {
        bindings.splice(existingIdx, 1);
    }

    const binding = parseShortcut(shortcut, commandId);
    bindings.push(binding);
}

/**
 * Get all registered commands (for command palette).
 */
export function getAllCommands(): Command[] {
    return [...commands.values()];
}

/**
 * Execute a command by ID.
 */
export function executeCommand(id: string): void {
    const cmd = commands.get(id);
    if (cmd) {
        cmd.execute();
    }
}

/**
 * Format a shortcut for display (platform-aware).
 */
export function formatShortcut(shortcut: string): string {
    if (isMac) {
        return shortcut
            .replace(/Cmd\+/gi, "⌘")
            .replace(/Ctrl\+/gi, "⌃")
            .replace(/Alt\+/gi, "⌥")
            .replace(/Shift\+/gi, "⇧");
    }
    return shortcut.replace(/Cmd\+/gi, "Ctrl+");
}

/**
 * Match a keyboard event against registered bindings.
 * Only matches when at least one modifier key (Meta/Ctrl/Alt) is pressed,
 * so normal typing is never intercepted.
 */
function matchEvent(e: KeyboardEvent): ShortcutBinding | null {
    // Don't match plain keystrokes or Shift-only combos — let the editor handle those
    if (!e.metaKey && !e.ctrlKey && !e.altKey) return null;

    for (const binding of bindings) {
        if (e.key.toLowerCase() !== binding.key) continue;

        const needsMeta = binding.modifiers.includes("meta");
        const needsCtrl = binding.modifiers.includes("ctrl");
        const needsAlt = binding.modifiers.includes("alt");
        const needsShift = binding.modifiers.includes("shift");

        // On Mac: "meta" → metaKey. On other platforms: "meta" → ctrlKey
        const metaOk = isMac ? e.metaKey === needsMeta : e.ctrlKey === needsMeta;
        const ctrlOk = isMac ? e.ctrlKey === needsCtrl : true;
        const altOk = e.altKey === needsAlt;
        const shiftOk = e.shiftKey === needsShift;

        if (metaOk && ctrlOk && altOk && shiftOk) {
            return binding;
        }
    }
    return null;
}

/**
 * React hook that installs a global keydown listener for shortcuts.
 *
 * Use capture phase so app shortcuts can be canceled early.
 * `matchEvent` only handles explicit app
 * shortcuts with modifiers, so normal editor typing still flows through.
 */
export function useKeyboardShortcuts(): void {
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            // Skip if another handler (e.g. CodeMirror keymap) already handled this event
            if (e.defaultPrevented) return;
            const binding = matchEvent(e);
            if (binding) {
                e.preventDefault();
                // Global shortcuts should fire once per physical key press.
                // Repeated keydown events while a modifier combo is held can
                // trigger rapid command repeats that the UI doesn't intend.
                if (e.repeat) return;
                executeCommand(binding.commandId);
            }
        };

        window.addEventListener("keydown", handler, true);
        return () => window.removeEventListener("keydown", handler, true);
    }, []);
}
